import { useKeycloak } from "@react-keycloak/web";
import { IGeneralInfo, IKatoItem, IKatoTreeItem, IStatistics, KatoType } from "../types/kato.types";
import {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
} from 'react';
import { getGeneralInfo, getStatistics } from "../requests/public.request";
import { oblast } from "../consts/kato.conts";
import { IScreeningInfo } from "../types/screening.types";
import { getScreening } from "../requests/screening.request";
import { useLocation, matchPath } from 'react-router-dom';
import { getKatoType } from "../helpers/utils";
import { getSupervisorKatoTree } from "../requests/supervisor.request";

interface KatoContextType {
    currentLevel: KatoType;
    selectedRegion: IKatoItem | null;
    selectedDistrict: IKatoItem | null;
    selectedVillage: IKatoItem | null;
    katoTree: IKatoTreeItem[];
    katoGeneralInfo: IGeneralInfo;
    screeningInfo: IScreeningInfo;
    statistics: Record<number, IStatistics>;

    setCurrentLevel: (level: KatoType) => void;
    setSelectedRegion: (item: IKatoItem | null) => void;
    setSelectedDistrict: (item: IKatoItem | null) => void;
    setSelectedVillage: (item: IKatoItem | null) => void;
    getCurrentKato: () => IKatoItem | null;
    updatedGeneralInfo: () => void;
    updateScreeningInfo: () => void;
}

const KatoContext = createContext<KatoContextType | undefined>(undefined);

export const KatoProvider = ({ children }: { children: ReactNode }) => {
    const { keycloak, initialized } = useKeycloak();
    const location = useLocation();
    const match = matchPath('/admin/:kato/*', location.pathname) || matchPath('/:kato/*', location.pathname)
    const kato = match?.params?.kato;

    const [currentLevel, setCurrentLevel] = useState<KatoType>(KatoType.OBLAST);
    const [selectedRegion, setSelectedRegion] = useState<IKatoItem | null>(null);
    const [selectedDistrict, setSelectedDistrict] = useState<IKatoItem | null>(null);
    const [selectedVillage, setSelectedVillage] = useState<IKatoItem | null>(null);
    const [katoTree, setKatoTree] = useState<IKatoTreeItem[]>([]);
    const [katoGeneralInfo, setKatoGeneralInfo] = useState<IGeneralInfo>({} as IGeneralInfo);
    const [screeningInfo, setScreeningInfo] = useState<IScreeningInfo>({} as IScreeningInfo);
    const [statistics, setStatistics] = useState<Record<number, IStatistics>>({} as Record<number, IStatistics>);

    const getCurrentKato = () => {
        switch (currentLevel) {
            case KatoType.OBLAST:
                return oblast;
            case KatoType.REGION:
                return selectedRegion;
            case KatoType.DISTRICT:
                return selectedDistrict;
            case KatoType.VILLAGE:
                return selectedVillage;
            default:
                return null;
        }
    }

    const updatedGeneralInfo = () => {
        const currentKato = kato && !isNaN(+kato) ? +kato : oblast.kato;
        getGeneralInfo(currentKato).then((res) => {
            setKatoGeneralInfo(res);
            setCurrentLevel(getKatoType(currentKato));
        });

        getStatistics().then((res) => {
            setStatistics(res);
        }).catch((err) => {
            console.error("Error fetching statistics:", err);
            setStatistics({} as Record<number, IStatistics>);
        });
    };

    const updateScreeningInfo = () => {
        const currentKato = kato && !isNaN(+kato) ? +kato : oblast.kato;
        getScreening(currentKato).then((res) => {
            setScreeningInfo(res);
        }).catch((err) => {
            console.error("Error fetching screening info:", err);
            setScreeningInfo({} as IScreeningInfo);
        });
    };

    useEffect(() => {
        if (!keycloak) return;
        if (keycloak.authenticated) {
            getSupervisorKatoTree().then((res) => {
                setKatoTree(res);
            });
        }
    }, [keycloak.authenticated])

    useEffect(() => {
        if (!kato || isNaN(+kato)) return;
        if (!initialized) return;

        updatedGeneralInfo();

        updateScreeningInfo();

    }, [initialized, kato]);

    useEffect(() => {
        if (!initialized) return;
        getStatistics().then((res) => {
            setStatistics(res);
        });
    }, [initialized, katoTree]);

    return (
        <KatoContext.Provider
            value={{
                currentLevel,
                selectedRegion,
                selectedDistrict,
                selectedVillage,
                setCurrentLevel,
                setSelectedRegion,
                setSelectedDistrict,
                setSelectedVillage,
                katoTree,
                katoGeneralInfo,
                getCurrentKato,
                screeningInfo,
                statistics,
                updatedGeneralInfo,
                updateScreeningInfo
            }}
        >
            {children}
        </KatoContext.Provider >
    );
};

export const useKato = (): KatoContextType => {
    const context = useContext(KatoContext);
    if (!context) {
        throw new Error('useKato must be used within a KatoProvider');
    }
    return context;
};